import React from 'react';
import { PiArrowLeft, PiCaretRight } from 'react-icons/pi';

import { Tooltip } from 'antd';

import OptionRow from './OptionRow';
import {
  getStepOptionId,
  PICKER_OPTION_ID_ATTRIBUTE,
  stepOptionsMatch,
  StepSelectorOption,
} from './stepConfig';

interface StepTwoPanelProps {
  primaryOptions: StepSelectorOption[];
  highlightedPrimary: StepSelectorOption | null;
  isSecondaryOpen: boolean;
  onPrimaryClick: (item: StepSelectorOption) => void;
  onBackToPrimaryList: () => void;

  // LEFT collapsed strip, shown once a primary item is chosen and the
  // secondary panel is open.
  primaryStripLabel: string;
  primaryStripIcon: React.ReactNode;
  primaryStripTooltip: string;

  // RIGHT expanded panel.
  backTooltip: string;
  secondaryContent: React.ReactNode;

  // RIGHT collapsed strip, shown when a primary item is highlighted but the
  // secondary panel has been closed (user went back to browse the list).
  secondaryStripActiveLabel: string;
  secondaryStripActiveIcon: React.ReactNode;
  secondaryStripActiveTooltip: string;
  onReopenSecondary: (primary: StepSelectorOption) => void;

  // RIGHT collapsed strip, shown when nothing has been highlighted yet.
  secondaryStripInertLabel: string;
  secondaryStripInertIcon: React.ReactNode;
  secondaryStripInertTooltip: string;
}

// Generic two-panel layout shared by SeedVarietyTwoPanel (crop -> variety)
// and SeedTreatmentTwoPanel (product -> target seed/rate/cost): a primary
// list on the left, a secondary detail panel on the right, sharing the
// picker's fixed width instead of a flyout portal so nothing overflows the
// screen. Picking a primary item collapses the left list to a narrow
// vertical-text strip and expands the right side; going back (the strip, or
// the right panel's own header) reverses that. What the right panel shows,
// and what a collapsed-but-highlighted right strip says, is fully owned by
// the caller — Seed's variety list and Seed Treatment's rate/target fields
// have nothing in common beyond this shell.
const StepTwoPanel: React.FC<StepTwoPanelProps> = ({
  primaryOptions,
  highlightedPrimary,
  isSecondaryOpen,
  onPrimaryClick,
  onBackToPrimaryList,
  primaryStripLabel,
  primaryStripIcon,
  primaryStripTooltip,
  backTooltip,
  secondaryContent,
  secondaryStripActiveLabel,
  secondaryStripActiveIcon,
  secondaryStripActiveTooltip,
  onReopenSecondary,
  secondaryStripInertLabel,
  secondaryStripInertIcon,
  secondaryStripInertTooltip,
}) => (
  <div className="mt-3 flex flex-1 gap-2 overflow-hidden">
    {/* LEFT: full primary list OR collapsed strip */}
    {isSecondaryOpen ? (
      <Tooltip title={primaryStripTooltip} placement="right">
        <button
          type="button"
          className="flex w-6 flex-shrink-0 cursor-pointer flex-col items-center justify-start gap-1 rounded-lg border border-gray-bright bg-success-light pt-3 transition-colors hover:brightness-95"
          onClick={onBackToPrimaryList}
        >
          {primaryStripIcon}
          <span className="[writing-mode:vertical-lr] select-none text-sm font-medium text-charcoal">
            {primaryStripLabel}
          </span>
        </button>
      </Tooltip>
    ) : (
      <div className="flex flex-1 flex-col overflow-hidden rounded-lg border border-gray-bright bg-white">
        <div className="flex-1 overflow-y-auto">
          {primaryOptions.map((item) => {
            const isHighlighted =
              !!highlightedPrimary &&
              stepOptionsMatch(highlightedPrimary, item);
            return (
              <OptionRow
                key={`${item.item}-${item.globalId ?? 'none'}`}
                label={item.label}
                metaOption={item}
                isHighlighted={isHighlighted}
                navAttributes={{
                  [PICKER_OPTION_ID_ATTRIBUTE]: getStepOptionId(item),
                }}
                onClick={() => onPrimaryClick(item)}
                trailing={
                  <PiCaretRight
                    size={14}
                    className="flex-shrink-0 self-center text-gray-medium"
                    aria-hidden="true"
                  />
                }
              />
            );
          })}
        </div>
      </div>
    )}

    {/* RIGHT: full secondary panel OR collapsed strip */}
    {isSecondaryOpen ? (
      <div className="flex flex-1 flex-col overflow-hidden rounded-lg border border-gray-bright bg-white">
        <Tooltip title={backTooltip} placement="bottom">
          <button
            type="button"
            className="flex flex-shrink-0 items-center gap-2 border-b border-gray-bright p-3 text-left text-base font-semibold text-charcoal transition-colors hover:bg-gray-smoke"
            onClick={onBackToPrimaryList}
          >
            <PiArrowLeft size={16} className="flex-shrink-0" />
            <span>{highlightedPrimary?.label}</span>
          </button>
        </Tooltip>
        {secondaryContent}
      </div>
    ) : highlightedPrimary ? (
      // A primary item is highlighted but the secondary panel is closed (user
      // went back). Make the strip clickable so they can re-open it.
      <Tooltip title={secondaryStripActiveTooltip} placement="left">
        <button
          type="button"
          className="flex w-6 flex-shrink-0 cursor-pointer flex-col items-center justify-start gap-1 rounded-lg border border-gray-bright bg-success-light pt-3 transition-colors hover:brightness-95"
          onClick={() => onReopenSecondary(highlightedPrimary)}
        >
          {secondaryStripActiveIcon}
          <span className="[writing-mode:vertical-lr] select-none text-sm font-medium text-charcoal">
            {secondaryStripActiveLabel}
          </span>
        </button>
      </Tooltip>
    ) : (
      <Tooltip title={secondaryStripInertTooltip} placement="left">
        <div className="flex w-6 flex-shrink-0 flex-col items-center justify-start gap-1 rounded-lg border border-gray-bright bg-gray-smoke pt-3">
          {secondaryStripInertIcon}
          <span className="[writing-mode:vertical-lr] select-none text-sm font-medium text-gray-muted">
            {secondaryStripInertLabel}
          </span>
        </div>
      </Tooltip>
    )}
  </div>
);

export default StepTwoPanel;
